/**
 * OrderTableItem (OrderTableItem.jsx)
 *
 * \src\components\IncomingOrders\IncomingOrdersTable\OrderTableItem.jsx
 */

import { memo, useMemo } from "react";
import { FormattedDate, useIntl } from "react-intl";
import {
  Badge,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ColumnType from "../helpers/ColumnType";
import mapOrder from "../helpers/orderMapper";
import ORDER_ROW_TYPE_META from "../helpers/types";
import ORDER_STATUSES from "../helpers/orderCategories";

const safeFormattedDate = (date) => date && <FormattedDate value={date} />;

/**
 * visibleFields-objektet styr vilka kolumner som visas.
 * Exempel:
 *  {
 *    orderType: true,
 *    ourOrderNo: true,
 *    ...
 *    rowCount: true,
 *  }
 */
const OrderTableItem = ({
  order: unmappedOrder,
  onShowOrderDetails,
  visibleFields = {},
}) => {
  const intl = useIntl();

  const order = useMemo(
    () => mapOrder(unmappedOrder),
    [unmappedOrder]
  );

  const show = (field) => visibleFields[field] !== false;

  const translateStatus = (status) => {
    if (!status) {
      return intl.formatMessage({ id: "incomingOrders.order.unknownShort" });
    }

    return intl.formatMessage(
      { id: `incomingOrders.orderstatus.${status}` },
      { defaultMessage: status }
    );
  };

  const StatusChip = ({ status }) => {
    const color = "#FFFFFF";

    return (
      <Chip
        label={translateStatus(status)}
        size="small"
        //color={color}
        variant={color === "default" ? "outlined" : "filled"}
      />
    );
  };

  function renderOrderRowCount() {
    const { orderRowCount } = order.derivedFields;

    return (
      <Chip
        label={intl.formatMessage(
          { id: "incomingOrders.order.rowCount" },
          { count: orderRowCount }
        )}
        size="small"
        variant="outlined"
      />
    );
  }

  function renderOrderRowProductSummary() {
    const { orderRowProductSummary } = order.derivedFields;
    const products = orderRowProductSummary.map((item) => item.product).filter(Boolean);

    return (
      <Tooltip
        title={
          <Typography sx={{ whiteSpace: "pre-line" }}>
            {products.join("\n")}
          </Typography>
        }
      >
        <Chip
          label={products.join(", ")}
          size="small"
          sx={{ maxWidth: "200px" }}
        />
      </Tooltip>
    );
  }

  function renderOrderRowSourceSummary() {
    // const { orderRowSourceSummary } = order.derivedFields;
    // const sources = orderRowSourceSummary.map((item) => item.source);
    return "Webshop";
    //return intl.formatMessage({ id: "incomingOrders.order.sourceWebshop" });
  }

  const PRODUCT_TYPE_INFO = ORDER_ROW_TYPE_META;


  function renderOrderRowTypeSummary() {
    const { orderRowTypeSummary = [] } = order.derivedFields;

    if (!orderRowTypeSummary.length) {
      return null;
    }

    const totalCount = orderRowTypeSummary.reduce(
      (acc, cur) => acc + cur.count,
      0
    );

    const uniqueTypes = [
      ...new Set(orderRowTypeSummary.map((x) => x.productType)),
    ];

    const isMixed = uniqueTypes.length > 1;

    const icon = isMixed
      ? "📚"
      : PRODUCT_TYPE_INFO[uniqueTypes[0]]?.icon ?? "❓";

    const label = isMixed
      ? "Blandade produkttyper"
      : PRODUCT_TYPE_INFO[uniqueTypes[0]]?.label ?? "Okänd";

    const tooltipText = isMixed
      ? orderRowTypeSummary
        .map((row) => {
          const info = PRODUCT_TYPE_INFO[row.productType];
          return `${row.count} ${info?.icon ?? "❓"} ${info?.label ?? row.productType}`;
        })
        .join("\n")
      : `${totalCount} ${icon} ${label}`;
        

    return (
      <Tooltip
        title={
          <Typography sx={{ whiteSpace: "pre-line" }}>
            {tooltipText}
          </Typography>
        }
      >
        <Badge
          badgeContent={totalCount}
          overlap="circular"
          slotProps={{
            badge: {
              sx: {
                background: "#ffff008f",
                color: "#888",
                m: -0.25,
              },
            },
          }}
        >
          <span>{icon}</span>
        </Badge>
      </Tooltip>
    );
  }

  const handleRowClick = (event) => {
    event.preventDefault();
    const { orderId, supplierOrderId } = order;
    onShowOrderDetails?.({ orderId, supplierOrderId });
  };

  return (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={handleRowClick}>
      {show(ColumnType.ORDER_TYPE) && (
        <TableCell>{renderOrderRowTypeSummary()}</TableCell>
      )}

      {show(ColumnType.SUPPLIER_ORDER_NO) && (
        <TableCell>{order.supplierOrderNo}</TableCell>
      )}

      {show(ColumnType.SUPPLIER_ORDER_REFERENCE) && (
        <TableCell>{order.supplierOrderReference}</TableCell>
      )}

      {false && show(ColumnType.ORDER_ID) && (
        <TableCell>{order.orderId}</TableCell>
      )}

      {show(ColumnType.ORDER_REFERENCE) && (
        <TableCell>{order.orderReference}</TableCell>
      )}

      {show(ColumnType.RECEIVER) && (
        <TableCell>{order.orderingCompany}</TableCell>
      )}

      {show(ColumnType.ORDER_DATE) && (
        <TableCell>{safeFormattedDate(order.orderDate)}</TableCell>
      )}

      {show(ColumnType.SUMMARY) && (
        <TableCell>{renderOrderRowProductSummary()}</TableCell>
      )}

      {show(ColumnType.DELIVERY_ADDRESS_2) && (
        <TableCell>{order.deliveryAddress?.address2}</TableCell>
      )}

      {show(ColumnType.DELIVERY_DATE) && (
        <TableCell>{safeFormattedDate(order.deliveryDateWish)}</TableCell>
      )}

      {show(ColumnType.ORDER_STATUS) && (
        <TableCell>
          <StatusChip status={order.orderStatus} />
        </TableCell>
      )}

      {show(ColumnType.SOURCE) && (
        <TableCell>{renderOrderRowSourceSummary()}</TableCell>
      )}                 

      {show(ColumnType.ROW_COUNT) && (
        <TableCell align="right">{renderOrderRowCount()}</TableCell>
      )}
    </TableRow>
  );
};

OrderTableItem.displayName = "OrderTableItem";

export default memo(OrderTableItem);
